/**
 * Approvals — user signups, new client registrations, client edit/delete requests, and payment verification.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
    Check, X, RefreshCw, Mail, MailCheck, ShieldCheck,
    UserPlus, ClipboardList, Edit3, Trash2, ExternalLink, Layers, Briefcase, Wallet, Loader2, Phone,
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import { useAuth } from '../../store/auth';
import { uploadHref } from '../../utils/uploadHref';

const TABS = [
    { id: 'all', label: 'All', icon: Layers },
    { id: 'users', label: 'User signups', icon: UserPlus },
    { id: 'clients', label: 'New clients', icon: ClipboardList },
    { id: 'payments', label: 'Payments', icon: Wallet },
    { id: 'tasks', label: 'Onboarding calls', icon: Phone },
    { id: 'designation', label: 'Designation', icon: Briefcase },
];

const inr = (v) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(Number(v || 0));

const TYPE_CHIP = {
    user: 'bg-violet-50 text-violet-800 ring-violet-200/80',
    client: 'bg-teal-50 text-teal-800 ring-teal-200/80',
    payment: 'bg-emerald-50 text-emerald-800 ring-emerald-200/80',
    update: 'bg-sky-50 text-sky-800 ring-sky-200/80',
    delete: 'bg-rose-50 text-rose-800 ring-rose-200/80',
    designation: 'bg-indigo-50 text-indigo-800 ring-indigo-200/80',
};

const FALLBACK_DESIGNATIONS = [
    { slug: 'bda', label: 'BDA — Business Development Associate' },
    { slug: 'bde', label: 'BDE — Business Development Executive' },
    { slug: 'bdm', label: 'BDM — Business Development Manager' },
    { slug: 'bhead', label: 'BHead — Business Head' },
    { slug: 'operations', label: 'Operations' },
    { slug: 'performance_marketing', label: 'Performance Marketing' },
    { slug: 'other', label: 'Other (non-revenue role)' },
];

function DesignationSelect({ value, onChange, designations, className }) {
    const options = designations.length > 0 ? designations : FALLBACK_DESIGNATIONS;
    return (
        <select className={className} value={value} onChange={e => onChange(e.target.value)}>
            <option value="">— pick designation —</option>
            {options.map(d => (
                <option key={d.slug} value={d.slug}>{d.label}</option>
            ))}
        </select>
    );
}

export default function Approvals() {
    const [searchParams] = useSearchParams();
    const user = useAuth(s => s.user);
    const canVerifyPayments = ['admin', 'finance'].includes(user?.role);

    const [tab, setTab] = useState(() => searchParams.get('tab') || 'all');
    const [users, setUsers] = useState([]);
    const [newClients, setNewClients] = useState([]);
    const [changeRequests, setChangeRequests] = useState([]);
    const [pendingPayments, setPendingPayments] = useState([]);
    const [adminTasks, setAdminTasks] = useState([]);
    const [designationChanges, setDesignationChanges] = useState([]);
    const [roles, setRoles] = useState([]);
    const [designations, setDesignations] = useState([]);
    const [busy, setBusy] = useState(false);
    const [edits, setEdits] = useState({});
    const [verifyTarget, setVerifyTarget] = useState(null);
    const [finProof, setFinProof] = useState(null);
    const [finDesc, setFinDesc] = useState('');
    const [verifyBusy, setVerifyBusy] = useState(false);

    const load = async () => {
        setBusy(true);
        try {
            const [userRes, formRes, desigRes] = await Promise.all([
                api.get('/users/queue'),
                api.get('/forms/approvals-queue'),
                api.get('/users/designation-change-queue'),
            ]);
            setUsers(userRes.data.items || []);
            setNewClients(formRes.data.newClients || []);
            setChangeRequests(formRes.data.changeRequests || []);
            setPendingPayments(formRes.data.pendingPayments || []);
            setAdminTasks(formRes.data.adminTasks || []);
            setDesignationChanges(desigRes.data.items || []);
        } finally {
            setBusy(false);
        }
    };

    useEffect(() => {
        load();
        api.get('/roles').then(r => setRoles(r.data.items)).catch(() => {});
        api.get('/auth/directory').then(r => setDesignations(r.data.designations || [])).catch(() => {});
    }, []);

    const getEdit = (u) => edits[u._id] || { salesDesignation: u.salesDesignation || '', role: u.role || 'sales_user' };
    const setEdit = (u, patch) => setEdits(e => ({ ...e, [u._id]: { ...getEdit(u), ...patch } }));

    const approveUser = async (u) => {
        if (!u.emailVerifiedAt) return toast.error('User has not verified their email yet.');
        const e = getEdit(u);
        if (!e.salesDesignation) return toast.error('Designation is required before approval.');
        try {
            await api.post(`/users/${u._id}/approve`, { salesDesignation: e.salesDesignation, role: e.role });
            toast.success(`${u.name} approved`);
            load();
        } catch { /* toasted */ }
    };

    const rejectUser = async (u) => {
        const reason = prompt(`Reject ${u.name}? Optional reason:`);
        if (reason === null) return;
        await api.post(`/users/${u._id}/reject`, { reason });
        toast.success(`${u.name} rejected`);
        load();
    };

    const reviewClient = async (submissionId, status) => {
        let reviewNote = '';
        if (status === 'rejected') reviewNote = prompt('Reason for rejection (optional):') || '';
        try {
            await api.post(`/forms/submissions/${submissionId}/review`, { status, reviewNote });
            toast.success(status === 'approved' ? 'Client approved' : 'Client rejected');
            load();
        } catch { /* toasted */ }
    };

    const reviewChange = async (row, decision) => {
        const reviewNote = decision === 'reject' ? (prompt('Reason for rejection (optional):') || '') : '';
        try {
            await api.post(`/forms/submissions/${row.submissionId}/change-requests/${row._id}/review`, {
                decision,
                reviewNote,
            });
            toast.success(decision === 'approve' ? 'Request approved' : 'Request rejected');
            load();
        } catch { /* toasted */ }
    };

    const reviewDesignation = async (row, decision) => {
        const reviewNote = decision === 'reject' ? (prompt('Reason for rejection (optional):') || '') : '';
        try {
            await api.post(`/users/${row.userId}/designation-change-requests/${row._id}/review`, {
                decision,
                reviewNote,
            });
            toast.success(decision === 'approve' ? 'Designation updated' : 'Request rejected');
            load();
        } catch { /* toasted */ }
    };

    const rejectPayment = async (row) => {
        const reviewNote = prompt('Reason for rejection (optional):') || '';
        try {
            await api.post(`/forms/submissions/${row.submissionId}/payments/${row._id}/reject`, { reviewNote });
            toast.success('Payment rejected');
            load();
        } catch { /* toasted */ }
    };

    const submitVerifyPayment = async (e) => {
        e.preventDefault();
        if (!verifyTarget) return;
        if (!finProof) {
            toast.error('Attach finance verification proof');
            return;
        }
        setVerifyBusy(true);
        try {
            const fd = new FormData();
            fd.append('financeProofDescription', finDesc);
            fd.append('financeProof', finProof);
            await api.post(
                `/forms/submissions/${verifyTarget.submissionId}/payments/${verifyTarget._id}/approve`,
                fd,
            );
            toast.success('Payment verified');
            setVerifyTarget(null);
            setFinProof(null);
            setFinDesc('');
            load();
        } catch { /* toasted */ }
        finally { setVerifyBusy(false); }
    };

    const completeTask = async (taskId) => {
        try {
            await api.post(`/admin-tasks/${taskId}/complete`);
            toast.success('Task marked done');
            load();
        } catch { /* toasted */ }
    };

    const totalPending = users.length + newClients.length + changeRequests.length + designationChanges.length + pendingPayments.length + adminTasks.length;

    const allRows = useMemo(() => {
        const rows = [];
        users.forEach(u => rows.push({ key: `user-${u._id}`, kind: 'user', sortAt: new Date(u.createdAt), u }));
        newClients.forEach(c => rows.push({ key: `client-${c._id}`, kind: 'client', sortAt: new Date(c.createdAt), c }));
        changeRequests.forEach(r => rows.push({
            key: `change-${r._id}`,
            kind: r.type === 'delete' ? 'delete' : 'update',
            sortAt: new Date(r.requestedAt),
            r,
        }));
        designationChanges.forEach(d => rows.push({
            key: `desig-${d._id}`,
            kind: 'designation',
            sortAt: new Date(d.requestedAt),
            d,
        }));
        pendingPayments.forEach(p => rows.push({
            key: `pay-${p._id}`,
            kind: 'payment',
            sortAt: new Date(p.recordedAt || p.paymentDate),
            p,
        }));
        return rows.sort((a, b) => b.sortAt - a.sortAt);
    }, [users, newClients, changeRequests, designationChanges, pendingPayments]);

    const typeLabel = (kind) => {
        if (kind === 'user') return 'User signup';
        if (kind === 'client') return 'New client';
        if (kind === 'payment') return 'Payment';
        if (kind === 'delete') return 'Delete client';
        if (kind === 'designation') return 'Designation change';
        return 'Edit client';
    };

    const typeIcon = (kind) => {
        if (kind === 'user') return UserPlus;
        if (kind === 'client') return ClipboardList;
        if (kind === 'payment') return Wallet;
        if (kind === 'delete') return Trash2;
        if (kind === 'designation') return Briefcase;
        return Edit3;
    };

    const formatDesigDelta = (d) => {
        const parts = [];
        const p = d.proposed || {};
        const prev = d.previous || {};
        if (String(p.designation || '') !== String(prev.designation || '')) {
            parts.push(`Title: ${prev.designation || '—'} → ${p.designation || '—'}`);
        }
        if (String(p.salesDesignation || '') !== String(prev.salesDesignation || '')) {
            parts.push(`Role: ${prev.salesDesignation || '—'} → ${p.salesDesignation || '—'}`);
        }
        if (String(p.officeBranch || '') !== String(prev.officeBranch || '')) {
            parts.push(`Office: ${prev.officeBranch || '—'} → ${p.officeBranch || '—'}`);
        }
        return parts.join(' · ') || '—';
    };

    return (
        <div className="space-y-4">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-2xl font-extrabold">Approvals</h1>
                    <p className="text-sm text-ink-500">
                        {totalPending} pending — signups, clients, payments, designation changes, and edit/delete requests.
                    </p>
                </div>
                <button type="button" className="ag-btn-ghost" onClick={load}>
                    <RefreshCw className={`h-4 w-4 ${busy ? 'animate-spin' : ''}`} /> Reload
                </button>
            </div>

            <div className="flex flex-wrap gap-2">
                {TABS.map(({ id, label, icon: Icon }) => {
                    const count = id === 'all' ? totalPending
                        : id === 'users' ? users.length
                        : id === 'clients' ? newClients.length
                        : id === 'payments' ? pendingPayments.length
                        : id === 'tasks' ? adminTasks.length
                        : id === 'designation' ? designationChanges.length
                        : changeRequests.length;
                    return (
                        <button
                            key={id}
                            type="button"
                            onClick={() => setTab(id)}
                            className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold transition ring-1 ${
                                tab === id
                                    ? 'bg-brand-teal text-white ring-brand-teal shadow-sm'
                                    : 'bg-white text-ink-700 ring-ink-200 hover:ring-brand-teal/40'
                            }`}
                        >
                            <Icon className="h-4 w-4" />
                            {label}
                            {count > 0 && (
                                <span className={`min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-bold grid place-items-center ${
                                    tab === id ? 'bg-white/20 text-white' : 'bg-amber-100 text-amber-900'
                                }`}>
                                    {count}
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>

            {tab === 'all' && (
                <div className="ag-card overflow-x-auto p-0">
                    <table className="ag-table">
                        <thead>
                            <tr>
                                <th>Type</th>
                                <th>Request</th>
                                <th>Details</th>
                                <th>When</th>
                                <th className="text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {allRows.map(row => {
                                const Icon = typeIcon(row.kind);
                                if (row.kind === 'user') {
                                    const u = row.u;
                                    const e = getEdit(u);
                                    const verified = !!u.emailVerifiedAt;
                                    return (
                                        <tr key={row.key} className="align-top">
                                            <td>
                                                <span className={`ag-chip ring-1 ${TYPE_CHIP.user}`}>
                                                    <Icon className="h-3 w-3 inline mr-1" /> {typeLabel(row.kind)}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="font-medium">{u.name}</div>
                                                <div className="text-xs text-ink-500">{u.email}</div>
                                            </td>
                                            <td className="text-xs max-w-[320px]">
                                                {verified ? (
                                                    <span className="ag-chip bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 mb-2 inline-flex">
                                                        <MailCheck className="h-3 w-3" /> Email verified
                                                    </span>
                                                ) : (
                                                    <span className="ag-chip bg-amber-50 text-amber-700 ring-1 ring-amber-200 mb-2 inline-flex">
                                                        <Mail className="h-3 w-3" /> Awaiting email verification
                                                    </span>
                                                )}
                                                <div className="flex flex-wrap gap-2 mt-1">
                                                    <DesignationSelect
                                                        className="ag-input min-w-[160px] text-xs flex-1"
                                                        designations={designations}
                                                        value={e.salesDesignation}
                                                        onChange={v => setEdit(u, { salesDesignation: v })}
                                                    />
                                                    <select className="ag-input min-w-[100px] text-xs" value={e.role} onChange={ev => setEdit(u, { role: ev.target.value })}>
                                                        {roles.map(r => <option key={r._id} value={r.slug}>{r.name}</option>)}
                                                    </select>
                                                </div>
                                            </td>
                                            <td className="text-xs text-ink-500 whitespace-nowrap">{row.sortAt.toLocaleString()}</td>
                                            <td>
                                                <div className="flex gap-2 justify-end">
                                                    <button type="button" className="ag-btn-primary text-xs" disabled={!verified} onClick={() => approveUser(u)}>
                                                        <ShieldCheck className="h-3.5 w-3.5" /> Approve
                                                    </button>
                                                    <button type="button" className="ag-btn-danger text-xs" onClick={() => rejectUser(u)}>
                                                        <X className="h-3.5 w-3.5" /> Reject
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                }
                                if (row.kind === 'client') {
                                    const c = row.c;
                                    return (
                                        <tr key={row.key} className="align-top">
                                            <td>
                                                <span className={`ag-chip ring-1 ${TYPE_CHIP.client}`}>
                                                    <Icon className="h-3 w-3 inline mr-1" /> {typeLabel(row.kind)}
                                                </span>
                                            </td>
                                            <td className="font-medium">{c.data?.name || 'Unnamed'}</td>
                                            <td className="text-xs text-ink-600">
                                                <div>By {c.submittedBy?.name || '—'}</div>
                                                <div>{inr(c.data?.dealAmount)} · {c.data?.phone || '—'}</div>
                                            </td>
                                            <td className="text-xs text-ink-500 whitespace-nowrap">{row.sortAt.toLocaleString()}</td>
                                            <td>
                                                <div className="flex gap-2 justify-end flex-wrap">
                                                    <Link to={`/admin/clients/${c._id}`} className="ag-btn-ghost text-xs">
                                                        <ExternalLink className="h-3.5 w-3.5" /> View
                                                    </Link>
                                                    <button type="button" className="ag-btn-primary text-xs" onClick={() => reviewClient(c._id, 'approved')}>
                                                        <Check className="h-3.5 w-3.5" /> Approve
                                                    </button>
                                                    <button type="button" className="ag-btn-danger text-xs" onClick={() => reviewClient(c._id, 'rejected')}>
                                                        <X className="h-3.5 w-3.5" /> Reject
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                }
                                if (row.kind === 'payment') {
                                    const p = row.p;
                                    return (
                                        <tr key={row.key} className="align-top">
                                            <td>
                                                <span className={`ag-chip ring-1 ${TYPE_CHIP.payment}`}>
                                                    <Icon className="h-3 w-3 inline mr-1" /> {typeLabel(row.kind)}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="font-medium">{p.clientName}</div>
                                                <div className="text-xs text-ink-500 tabular-nums">{inr(p.amount)}</div>
                                            </td>
                                            <td className="text-xs text-ink-600">
                                                <div>By {p.addedBy?.name || '—'}</div>
                                                <div>{p.mode || '—'} · {p.transactionId || 'No txn ID'}</div>
                                                {p.proof?.path && (
                                                    <a href={uploadHref(p.proof.path)} target="_blank" rel="noreferrer" className="text-brand-teal-700 hover:underline mt-0.5 inline-block">
                                                        Sales proof
                                                    </a>
                                                )}
                                            </td>
                                            <td className="text-xs text-ink-500 whitespace-nowrap">{row.sortAt.toLocaleString()}</td>
                                            <td>
                                                <div className="flex gap-2 justify-end flex-wrap">
                                                    <Link to={`/admin/clients/${p.submissionId}`} className="ag-btn-ghost text-xs">
                                                        <ExternalLink className="h-3.5 w-3.5" /> View
                                                    </Link>
                                                    {canVerifyPayments && (
                                                        <>
                                                            <button type="button" className="ag-btn-primary text-xs" onClick={() => setVerifyTarget(p)}>
                                                                <Check className="h-3.5 w-3.5" /> Verify
                                                            </button>
                                                            <button type="button" className="ag-btn-danger text-xs" onClick={() => rejectPayment(p)}>
                                                                <X className="h-3.5 w-3.5" /> Reject
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                }
                                if (row.kind === 'designation') {
                                    const d = row.d;
                                    return (
                                        <tr key={row.key} className="align-top">
                                            <td>
                                                <span className={`ag-chip ring-1 ${TYPE_CHIP.designation}`}>
                                                    <Icon className="h-3 w-3 inline mr-1" /> {typeLabel(row.kind)}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="font-medium">{d.userName}</div>
                                                <div className="text-xs text-ink-500">{d.userEmail}</div>
                                            </td>
                                            <td className="text-xs text-ink-600 max-w-[320px]">{formatDesigDelta(d)}</td>
                                            <td className="text-xs text-ink-500 whitespace-nowrap">{row.sortAt.toLocaleString()}</td>
                                            <td>
                                                <div className="flex gap-2 justify-end flex-wrap">
                                                    <Link to={`/admin/users/${d.userId}`} className="ag-btn-ghost text-xs">
                                                        <ExternalLink className="h-3.5 w-3.5" /> Profile
                                                    </Link>
                                                    <button type="button" className="ag-btn-primary text-xs" onClick={() => reviewDesignation(d, 'approve')}>
                                                        <Check className="h-3.5 w-3.5" /> Approve
                                                    </button>
                                                    <button type="button" className="ag-btn-danger text-xs" onClick={() => reviewDesignation(d, 'reject')}>
                                                        <X className="h-3.5 w-3.5" /> Reject
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                }
                                const r = row.r;
                                return (
                                    <tr key={row.key} className="align-top">
                                        <td>
                                            <span className={`ag-chip ring-1 ${TYPE_CHIP[row.kind]}`}>
                                                <Icon className="h-3 w-3 inline mr-1" /> {typeLabel(row.kind)}
                                            </span>
                                        </td>
                                        <td className="font-medium">{r.clientName}</td>
                                        <td className="text-xs text-ink-600 max-w-[280px]">
                                            <div>By {r.requestedBy?.name || '—'}</div>
                                            {row.kind === 'update' ? (
                                                <div className="mt-0.5" title={Object.keys(r.patch || {}).join(', ')}>
                                                    Fields: {Object.keys(r.patch || {}).join(', ') || '—'}
                                                </div>
                                            ) : (
                                                <div className="italic mt-0.5">{r.reason || '—'}</div>
                                            )}
                                            {row.kind === 'update' && r.reason && (
                                                <div className="text-ink-500 mt-0.5">{r.reason}</div>
                                            )}
                                        </td>
                                        <td className="text-xs text-ink-500 whitespace-nowrap">{row.sortAt.toLocaleString()}</td>
                                        <td>
                                            <div className="flex gap-2 justify-end flex-wrap">
                                                <Link to={`/admin/clients/${r.submissionId}`} className="ag-btn-ghost text-xs">
                                                    <ExternalLink className="h-3.5 w-3.5" /> View
                                                </Link>
                                                <button type="button" className="ag-btn-primary text-xs" onClick={() => reviewChange(r, 'approve')}>
                                                    <Check className="h-3.5 w-3.5" /> Approve
                                                </button>
                                                <button type="button" className="ag-btn-danger text-xs" onClick={() => reviewChange(r, 'reject')}>
                                                    <X className="h-3.5 w-3.5" /> Reject
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                            {allRows.length === 0 && (
                                <tr><td colSpan={5} className="text-center text-ink-500 py-8">No pending approvals.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {tab === 'users' && (
                <div className="ag-card overflow-x-auto p-0">
                    <table className="ag-table">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Email</th>
                                <th>Verified</th>
                                <th>Designation</th>
                                <th>Role</th>
                                <th>Signed up</th>
                                <th className="text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map(u => {
                                const e = getEdit(u);
                                const verified = !!u.emailVerifiedAt;
                                return (
                                    <tr key={u._id} className="align-top">
                                        <td className="font-medium">{u.name}</td>
                                        <td className="text-xs">{u.email}</td>
                                        <td>
                                            {verified ? (
                                                <span className="ag-chip bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200">
                                                    <MailCheck className="h-3 w-3" /> verified
                                                </span>
                                            ) : (
                                                <span className="ag-chip bg-amber-50 text-amber-700 ring-1 ring-amber-200">
                                                    <Mail className="h-3 w-3" /> waiting
                                                </span>
                                            )}
                                        </td>
                                        <td>
                                            <DesignationSelect
                                                className="ag-input min-w-[180px] text-xs"
                                                designations={designations}
                                                value={e.salesDesignation}
                                                onChange={v => setEdit(u, { salesDesignation: v })}
                                            />
                                        </td>
                                        <td>
                                            <select className="ag-input min-w-[120px] text-xs" value={e.role} onChange={ev => setEdit(u, { role: ev.target.value })}>
                                                {roles.map(r => <option key={r._id} value={r.slug}>{r.name}</option>)}
                                            </select>
                                        </td>
                                        <td className="text-xs text-ink-500">{new Date(u.createdAt).toLocaleString()}</td>
                                        <td>
                                            <div className="flex gap-2 justify-end">
                                                <button type="button" className="ag-btn-primary text-xs" disabled={!verified} onClick={() => approveUser(u)}>
                                                    <ShieldCheck className="h-3.5 w-3.5" /> Approve
                                                </button>
                                                <button type="button" className="ag-btn-danger text-xs" onClick={() => rejectUser(u)}>
                                                    <X className="h-3.5 w-3.5" /> Reject
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                            {users.length === 0 && (
                                <tr><td colSpan={7} className="text-center text-ink-500 py-8">No pending user signups.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {tab === 'clients' && (
                <div className="ag-card overflow-x-auto p-0">
                    <table className="ag-table">
                        <thead>
                            <tr>
                                <th>Client</th>
                                <th>Submitted by</th>
                                <th>Deal</th>
                                <th>Phone</th>
                                <th>Submitted</th>
                                <th className="text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {newClients.map(c => (
                                <tr key={c._id} className="align-top">
                                    <td className="font-medium">{c.data?.name || 'Unnamed'}</td>
                                    <td className="text-xs">{c.submittedBy?.name || '—'}</td>
                                    <td className="text-xs tabular-nums">{inr(c.data?.dealAmount)}</td>
                                    <td className="text-xs">{c.data?.phone || '—'}</td>
                                    <td className="text-xs text-ink-500">{new Date(c.createdAt).toLocaleString()}</td>
                                    <td>
                                        <div className="flex gap-2 justify-end flex-wrap">
                                            <Link to={`/admin/clients/${c._id}`} className="ag-btn-ghost text-xs">
                                                <ExternalLink className="h-3.5 w-3.5" /> View
                                            </Link>
                                            <button type="button" className="ag-btn-primary text-xs" onClick={() => reviewClient(c._id, 'approved')}>
                                                <Check className="h-3.5 w-3.5" /> Approve
                                            </button>
                                            <button type="button" className="ag-btn-danger text-xs" onClick={() => reviewClient(c._id, 'rejected')}>
                                                <X className="h-3.5 w-3.5" /> Reject
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {newClients.length === 0 && (
                                <tr><td colSpan={6} className="text-center text-ink-500 py-8">No new clients waiting for approval.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {tab === 'payments' && (
                <div className="ag-card overflow-x-auto p-0">
                    <table className="ag-table">
                        <thead>
                            <tr>
                                <th>Client</th>
                                <th>Amount</th>
                                <th>Recorded by</th>
                                <th>Details</th>
                                <th>Recorded</th>
                                <th className="text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {pendingPayments.map(p => (
                                <tr key={p._id} className="align-top">
                                    <td className="font-medium">{p.clientName}</td>
                                    <td className="text-sm font-bold tabular-nums">{inr(p.amount)}</td>
                                    <td className="text-xs">{p.addedBy?.name || '—'}</td>
                                    <td className="text-xs text-ink-600">
                                        <div>{p.mode || '—'}</div>
                                        <div className="font-mono text-[11px] text-ink-500">{p.transactionId || '—'}</div>
                                        {p.paymentDate && (
                                            <div className="text-ink-500">Payment date: {new Date(p.paymentDate).toLocaleDateString('en-IN')}</div>
                                        )}
                                        {p.proof?.path && (
                                            <a href={uploadHref(p.proof.path)} target="_blank" rel="noreferrer" className="text-brand-teal-700 hover:underline mt-0.5 inline-block">
                                                Sales proof
                                            </a>
                                        )}
                                    </td>
                                    <td className="text-xs text-ink-500 whitespace-nowrap">
                                        {new Date(p.recordedAt || p.paymentDate).toLocaleString()}
                                    </td>
                                    <td>
                                        <div className="flex gap-2 justify-end flex-wrap">
                                            <Link to={`/admin/clients/${p.submissionId}`} className="ag-btn-ghost text-xs">
                                                <ExternalLink className="h-3.5 w-3.5" /> View
                                            </Link>
                                            {canVerifyPayments ? (
                                                <>
                                                    <button type="button" className="ag-btn-primary text-xs" onClick={() => setVerifyTarget(p)}>
                                                        <Check className="h-3.5 w-3.5" /> Verify
                                                    </button>
                                                    <button type="button" className="ag-btn-danger text-xs" onClick={() => rejectPayment(p)}>
                                                        <X className="h-3.5 w-3.5" /> Reject
                                                    </button>
                                                </>
                                            ) : (
                                                <span className="text-[11px] text-ink-400 self-center">Finance verification required</span>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {pendingPayments.length === 0 && (
                                <tr><td colSpan={6} className="text-center text-ink-500 py-8">No payments pending verification.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {tab === 'tasks' && (
                <div className="space-y-4">
                    <p className="text-sm text-ink-600">
                        Created automatically when a new client registers. Call the client to schedule their <b>Client Onboarding Call</b>.
                    </p>
                    {adminTasks.length === 0 ? (
                        <div className="ag-card p-8 text-center text-sm text-ink-500">
                            No open onboarding calls. New tasks appear when sales registers a client.
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            {adminTasks.map(t => (
                                <div key={t.id} className="ag-card p-4 space-y-3">
                                    <div className="flex items-start justify-between gap-3">
                                        <div>
                                            <p className="font-semibold text-ink-900">{t.title}</p>
                                            <p className="text-xs text-ink-500 mt-1">{t.description}</p>
                                        </div>
                                        <span className="ag-chip bg-amber-50 text-amber-900 text-[10px] shrink-0">Open</span>
                                    </div>
                                    <div className="flex flex-wrap gap-3 text-xs text-ink-600">
                                        {t.clientPhone && (
                                            <a href={`tel:${t.clientPhone}`} className="inline-flex items-center gap-1 text-brand-teal-700 font-medium">
                                                <Phone className="h-3.5 w-3.5" /> {t.clientPhone}
                                            </a>
                                        )}
                                        {t.clientEmail && (
                                            <a href={`mailto:${t.clientEmail}`} className="inline-flex items-center gap-1 text-brand-teal-700 font-medium">
                                                <Mail className="h-3.5 w-3.5" /> {t.clientEmail}
                                            </a>
                                        )}
                                    </div>
                                    <div className="flex flex-wrap gap-2 pt-1">
                                        {t.refId && (
                                            <Link to={`/admin/clients/${t.refId}`} className="ag-btn-ghost text-xs">
                                                <ExternalLink className="h-3.5 w-3.5" /> Open client
                                            </Link>
                                        )}
                                        <button type="button" className="ag-btn-primary text-xs" onClick={() => completeTask(t.id)}>
                                            <Check className="h-3.5 w-3.5" /> Mark call done
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {tab === 'changes' && (
                <div className="ag-card overflow-x-auto p-0">
                    <table className="ag-table">
                        <thead>
                            <tr>
                                <th>Type</th>
                                <th>Client</th>
                                <th>Requested by</th>
                                <th>Details</th>
                                <th>When</th>
                                <th className="text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {changeRequests.map(r => (
                                <tr key={r._id} className="align-top">
                                    <td>
                                        <span className={`ag-chip ${r.type === 'delete' ? 'bg-rose-50 text-rose-800' : 'bg-sky-50 text-sky-800'}`}>
                                            {r.type === 'delete' ? <Trash2 className="h-3 w-3 inline mr-1" /> : <Edit3 className="h-3 w-3 inline mr-1" />}
                                            {r.type}
                                        </span>
                                    </td>
                                    <td className="font-medium">{r.clientName}</td>
                                    <td className="text-xs">{r.requestedBy?.name || '—'}</td>
                                    <td className="text-xs text-ink-600 max-w-[280px]">
                                        {r.type === 'update' ? (
                                            <span title={Object.keys(r.patch || {}).join(', ')}>
                                                Fields: {Object.keys(r.patch || {}).join(', ') || '—'}
                                            </span>
                                        ) : (
                                            <span className="italic">{r.reason || '—'}</span>
                                        )}
                                        {r.type === 'update' && r.reason && (
                                            <div className="text-ink-500 mt-0.5">{r.reason}</div>
                                        )}
                                    </td>
                                    <td className="text-xs text-ink-500 whitespace-nowrap">{new Date(r.requestedAt).toLocaleString()}</td>
                                    <td>
                                        <div className="flex gap-2 justify-end flex-wrap">
                                            <Link to={`/admin/clients/${r.submissionId}`} className="ag-btn-ghost text-xs">
                                                <ExternalLink className="h-3.5 w-3.5" /> View
                                            </Link>
                                            <button type="button" className="ag-btn-primary text-xs" onClick={() => reviewChange(r, 'approve')}>
                                                <Check className="h-3.5 w-3.5" /> Approve
                                            </button>
                                            <button type="button" className="ag-btn-danger text-xs" onClick={() => reviewChange(r, 'reject')}>
                                                <X className="h-3.5 w-3.5" /> Reject
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {changeRequests.length === 0 && (
                                <tr><td colSpan={6} className="text-center text-ink-500 py-8">No edit or delete requests pending.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {tab === 'designation' && (
                <div className="ag-card overflow-x-auto p-0">
                    <table className="ag-table">
                        <thead>
                            <tr>
                                <th>User</th>
                                <th>Changes</th>
                                <th>When</th>
                                <th className="text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {designationChanges.map(d => (
                                <tr key={d._id} className="align-top">
                                    <td>
                                        <div className="font-medium">{d.userName}</div>
                                        <div className="text-xs text-ink-500">{d.userEmail} · {d.userRole}</div>
                                    </td>
                                    <td className="text-xs text-ink-600 max-w-[360px]">{formatDesigDelta(d)}</td>
                                    <td className="text-xs text-ink-500 whitespace-nowrap">{new Date(d.requestedAt).toLocaleString()}</td>
                                    <td>
                                        <div className="flex gap-2 justify-end flex-wrap">
                                            <Link to={`/admin/users/${d.userId}`} className="ag-btn-ghost text-xs">
                                                <ExternalLink className="h-3.5 w-3.5" /> Profile
                                            </Link>
                                            <button type="button" className="ag-btn-primary text-xs" onClick={() => reviewDesignation(d, 'approve')}>
                                                <Check className="h-3.5 w-3.5" /> Approve
                                            </button>
                                            <button type="button" className="ag-btn-danger text-xs" onClick={() => reviewDesignation(d, 'reject')}>
                                                <X className="h-3.5 w-3.5" /> Reject
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {designationChanges.length === 0 && (
                                <tr><td colSpan={4} className="text-center text-ink-500 py-8">No designation changes pending.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {verifyTarget && canVerifyPayments && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-ink-900/50 backdrop-blur-sm" role="dialog" aria-modal="true" aria-labelledby="verify-pay-title">
                    <div className="w-full max-w-md rounded-2xl bg-white shadow-xl ring-1 ring-ink-200 p-6 space-y-4">
                        <h3 id="verify-pay-title" className="text-lg font-extrabold text-ink-900">Verify payment</h3>
                        <p className="text-sm text-ink-600">
                            {inr(verifyTarget.amount)} for <b>{verifyTarget.clientName}</b> — upload finance verification proof to approve.
                        </p>
                        <form onSubmit={submitVerifyPayment} className="space-y-3">
                            <label className="block">
                                <span className="ag-label">Finance proof file</span>
                                <input className="ag-input" type="file" accept="image/*,.pdf" required onChange={e => setFinProof(e.target.files?.[0] || null)} />
                            </label>
                            <label className="block">
                                <span className="ag-label">Description (optional)</span>
                                <textarea className="ag-input min-h-[80px]" value={finDesc} onChange={e => setFinDesc(e.target.value)} />
                            </label>
                            <div className="flex gap-2 justify-end pt-2">
                                <button type="button" className="ag-btn-ghost" onClick={() => { setVerifyTarget(null); setFinProof(null); setFinDesc(''); }}>Cancel</button>
                                <button type="submit" className="ag-btn-primary" disabled={verifyBusy}>
                                    {verifyBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                                    {verifyBusy ? 'Saving…' : 'Approve'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

        </div>
    );
}
